import { makeAutoObservable } from 'mobx';
import { IPresenterSlideEntity } from '../presenter-slide-entity/presenter-slide-entity';
import { ITERATE_NUMBER } from '../../../../constans/polls-constants';
import SequenceEntity from '../sequence-entity/sequence-entity';
import { buildPresenterSlideItemsFromSegmentsArray } from '../../../../utils/helpers/presenter-slides-helpers';
import { PresenterActivePointer } from '../presenter-active-pointer/presenter-active-pointer';

/** This Class was written for work with Slide Deck in case if there will be need to work with other Knovio
 * feature that are consist from config aka KVX object (for example On Demand Knovio);*/

class SlideManagementEntity {
  private slides: IPresenterSlideEntity[];
  private activePointer: PresenterActivePointer;

  constructor (
        private readonly additionalIndex: number = ITERATE_NUMBER
  ) {
    makeAutoObservable(this);
    this.slides = [];
  }

  private parseSegmentsFromSequence = (sequence: SequenceEntity): void => {
    this.slides = buildPresenterSlideItemsFromSegmentsArray(sequence.getSegments());
  };

  public initSlides = (sequence: SequenceEntity, cuePointId = ''): void => {
    this.parseSegmentsFromSequence(sequence);
    if(!this.slides.length) return;
    const preselectedSlide = this.getSlideByCuePointId(cuePointId);
    const preselectedActiveSlide: IPresenterSlideEntity = preselectedSlide ? preselectedSlide : this.slides[0];
    this.activePointer.setActiveItem(preselectedActiveSlide);
  };

  public getSlides = (): IPresenterSlideEntity[] => {
    return this.slides;
  };

  public getNumberOfSlides = (): number => {
    return this.slides.length;
  };

  public getActivePointer = (): PresenterActivePointer => {
    return this.activePointer;
  };

  public setCurrentSlide = (slide: IPresenterSlideEntity): void => {
    this.activePointer.setActiveItem(slide);
  };

  public isSelectPrevSlideDisabled = (): boolean => {
    const firstIndexInArray = 0;
    return this.activePointer.getCurrentCuePointIndex() <= firstIndexInArray;
  };

  public isSelectNextSlideDisabled = (): boolean => {
    return this.activePointer.getCurrentCuePointIndex() >= this.getNumberOfSlides() - this.additionalIndex;
  };

  public addSlides = (slidesToAdd: IPresenterSlideEntity[], atIndex: number | null): void => {
    const currentActiveItem = this.activePointer.getActiveItem();
    const wasEmpty = this.slides.length === 0;
    if (atIndex === null) {
      this.slides.push(...slidesToAdd);
    } else {
      this.slides.splice(atIndex, 0, ...slidesToAdd);
    }
    const isActiveItemValid = currentActiveItem && this.slides.some(
      slide => slide.getCuePointId() === currentActiveItem.getCuePointId()
    );
    if (wasEmpty || !isActiveItemValid) {
      this.activePointer.setActiveItem(this.slides[0]);
    }
    this.activePointer.updateCurrentIndexAfterWSChanges();
  };

  public removeSlide = (slideToRemove: IPresenterSlideEntity): void => {
    const wasActiveSlide = this.activePointer.getActiveItem()?.getCuePointId() === slideToRemove.getCuePointId();
    this.slides = this.slides.filter((slide)=> slide.getCuePointId() !== slideToRemove.getCuePointId());
    if (wasActiveSlide && this.slides.length > 0) {
      this.activePointer.setActiveItem(this.slides[0]);
    } else if (!wasActiveSlide) {
      this.activePointer.updateCurrentIndexAfterWSChanges();
    }
  };

  public reorderSlide = (slideToReorder: IPresenterSlideEntity, atIndex: number | null): void => {
    if (atIndex === null) return;
    this.slides = this.slides.filter((presenterSlide)=> presenterSlide.getCuePointId() !== slideToReorder.getCuePointId());
    this.slides.splice(atIndex, 0, slideToReorder);
    this.activePointer.updateCurrentIndexAfterWSChanges();
  };

  public updateSlideMetadata = (slideToUpdate: IPresenterSlideEntity): void => {
    const presenterSlideToUpdate = this.getSlideByCuePointId(slideToUpdate.getCuePointId());
    if (presenterSlideToUpdate) {
      presenterSlideToUpdate.setCuePointMetadata(slideToUpdate.getCuePointMetadata());
      if(presenterSlideToUpdate.getCuePointId() === this.activePointer.getCurrentCuePointId()) {
        this.activePointer.getActiveItem().setCuePointMetadata(slideToUpdate.getCuePointMetadata());
      }
    }
  };

  public selectSlide = (index:number): void => {
    this.activePointer.setActiveItem(this.slides[index]);
  };

  public getSlideByIndex = (index: number): IPresenterSlideEntity => {
    return this.slides[index];
  };

  public getSlideByCuePointId = (id: string): IPresenterSlideEntity | void => {
    return this.slides.find((slide)=> slide.getCuePointId() === id);
  };

  public setActivePointer = (activePointer: PresenterActivePointer): void => {
    this.activePointer = activePointer;
  };

  public purgeSlides = (): void => {
    this.slides = [];
  };
}

export default SlideManagementEntity;
